import React, { useState, useEffect } from 'react';
import { awardsAPI } from '../../utils/api';
import Loading from '../common/Loading';
import ErrorMessage from '../common/ErrorMessage';
import { FaTrophy } from 'react-icons/fa';
import useScrollAnimation from '../../hooks/useScrollAnimation';

const Awards = () => {
  const [awards, setAwards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Scroll animations
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: timelineRef } = useScrollAnimation();

  useEffect(() => {
    const fetchAwards = async () => {
      try {
        setLoading(true);
        const response = await awardsAPI.getAll();

        // Handle response structure
        const awardsData = response.data.data || response.data || [];

        // Sort by sortOrder
        const sortedAwards = Array.isArray(awardsData)
          ? awardsData.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
          : [];

        setAwards(sortedAwards);
        setError(null);
      } catch (err) {
        console.error('Error fetching awards:', err);
        setError(err.message || 'Failed to load awards');
        setAwards([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAwards();
  }, []);

  if (loading) {
    return (
      <section className="py-20 bg-bg-secondary">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center">
            <Loading size="lg" text="Loading awards..." />
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-20 bg-bg-secondary">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <ErrorMessage message={error} />
        </div>
      </section>
    );
  }

  if (awards.length === 0) {
    return null;
  }

  return (
    <section className="py-20 md:py-28 bg-bg-secondary">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          {/* Header with fade-in */}
          <div
            ref={headerRef}
            className={`text-center mb-16 md:mb-20 transition-all duration-700 ease-out ${
              headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <h2 className="text-4xl md:text-5xl lg:text-display font-heading font-bold text-secondary mb-6 leading-tight">
              Awards & Recognition
            </h2>
            <p className="text-lg md:text-xl text-text-secondary font-body leading-relaxed">
              Honored achievements and milestones
            </p>
          </div>

          {/* Awards List */}
          <div ref={timelineRef} className="relative">
            {/* Awards */}
            <div className="space-y-6 md:space-y-8">
              {awards.map((award, index) => (
                <div
                  key={award._id}
                  className="mb-6 md:mb-8"
                >

                  {/* Content - simplified layout */}
                  <div className="max-w-4xl mx-auto">
                    {/* Award Card */}
                    <div className="bg-white p-6 md:p-8 rounded-sm shadow-soft hover:shadow-soft-lg transition-all duration-500">
                      <div className="flex items-start gap-4 md:gap-6 mb-4">
                        <div className="w-12 h-12 md:w-16 md:h-16 bg-accent/10 rounded-full flex items-center justify-center flex-shrink-0">
                          <FaTrophy className="text-accent text-xl md:text-2xl" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-start justify-between gap-4 mb-3">
                            <h3 className="text-xl md:text-2xl font-heading font-bold text-secondary leading-tight flex-1">
                              {award.title}
                            </h3>
                            {award.year && (
                              <span className="text-2xl md:text-3xl font-heading font-bold text-accent flex-shrink-0">
                                {award.year}
                              </span>
                            )}
                          </div>
                          <p className="text-text-secondary font-body leading-relaxed">
                            {award.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Awards;
